export interface OrderbookLevel {
  price: number;
  size: number;
  total: number;
}

export interface OrderbookData {
  bids: OrderbookLevel[];
  asks: OrderbookLevel[];
  timestamp: number;
}

type OrderbookCallback = (data: OrderbookData) => void;

export class OrderbookWebSocket {
  private ws: WebSocket | null = null;
  private callbacks: Set<OrderbookCallback> = new Set();
  private currentSymbol: string | null = null;
  private currentDex: string | null = null;
  private reconnectTimer: number | null = null;
  private isConnecting: boolean = false;
  private wsUrl: string;

  constructor(wsUrl: string = 'wss://api.hyperliquid.xyz/ws') {
    this.wsUrl = wsUrl;
  }

  subscribe(symbol: string, dex: string | undefined, callback: OrderbookCallback) {
    this.callbacks.add(callback);

    // If already connected to the same market, just add callback
    if (this.currentSymbol === symbol && this.currentDex === dex && this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    const oldSymbol = this.currentSymbol;
    this.currentSymbol = symbol;
    this.currentDex = dex || null;

    // If WebSocket is already connected, just swap subscriptions (faster than reconnecting)
    if (this.ws?.readyState === WebSocket.OPEN && oldSymbol) {
      // Unsubscribe from old market
      const unsubscribeMessage = {
        method: 'unsubscribe',
        subscription: {
          type: 'l2Book',
          coin: oldSymbol,
        },
      };
      this.ws.send(JSON.stringify(unsubscribeMessage));
      console.log('Unsubscribed from orderbook:', oldSymbol);

      // Subscribe to new market
      const subscribeMessage = {
        method: 'subscribe',
        subscription: {
          type: 'l2Book',
          coin: symbol,
        },
      };
      this.ws.send(JSON.stringify(subscribeMessage));
      console.log('Subscribed to orderbook:', symbol);
    } else {
      // No existing connection, create new one
      this.connect();
    }
  }

  unsubscribe(callback: OrderbookCallback) {
    this.callbacks.delete(callback);

    // If no more callbacks, disconnect
    if (this.callbacks.size === 0) {
      this.disconnect();
    }
  }

  private connect() {
    if (this.isConnecting || !this.currentSymbol) return;

    this.isConnecting = true;
    console.log('Connecting to orderbook WebSocket for:', this.currentSymbol, 'dex:', this.currentDex);

    try {
      this.ws = new WebSocket(this.wsUrl);

      // Store reference to current WebSocket to prevent race conditions
      const currentWs = this.ws;
      const subscribedSymbol = this.currentSymbol;

      currentWs.onopen = () => {
        // Only proceed if this WebSocket is still the active one
        if (this.ws !== currentWs) {
          console.log('WebSocket opened but no longer active, ignoring');
          currentWs.close();
          return;
        }

        console.log('WebSocket connected');
        this.isConnecting = false;

        // NOTE: According to Hyperliquid WS docs (https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/websocket/subscriptions)
        // l2Book subscription format is: { "type": "l2Book", "coin": "<coin_symbol>" }
        // The docs do NOT mention a separate "dex" parameter for l2Book subscriptions.
        // For HIP-3 markets, use the full symbol including dex prefix (e.g., "xyz:XYZ100")
        // For native markets, use just the symbol (e.g., "BTC")
        const coinName = this.currentSymbol!;

        // Subscribe to L2 book updates
        const subscribeMessage = {
          method: 'subscribe',
          subscription: {
            type: 'l2Book',
            coin: coinName,
          },
        };

        this.ws?.send(JSON.stringify(subscribeMessage));
        console.log('Subscribed to orderbook:', subscribeMessage);
      };

      currentWs.onmessage = (event) => {
        // Only process messages if this WebSocket is still the active one and for the correct symbol
        if (this.ws !== currentWs || this.currentSymbol !== subscribedSymbol) {
          return;
        }
        try {
          const message = JSON.parse(event.data);

          // Handle L2 book updates
          if (message.channel === 'l2Book' && message.data) {
            const data = message.data;

            // Parse bids (best bid at top, cumulative grows downward)
            const bids = data.levels[0].map((level: any, index: number) => {
              const price = parseFloat(level.px);
              const size = parseFloat(level.sz);
              // Calculate cumulative total from best bid downward
              let total = size;
              for (let i = 0; i < index; i++) {
                total += parseFloat(data.levels[0][i].sz);
              }
              return { price, size, total };
            });

            // Parse asks - keep in ascending order (best/lowest ask first)
            // Hyperliquid API returns asks sorted ascending (best ask first)
            const asks = data.levels[1].map((level: any, index: number) => {
              const price = parseFloat(level.px);
              const size = parseFloat(level.sz);
              // Calculate cumulative total from best ask (index 0) outward
              let total = size;
              for (let i = 0; i < index; i++) {
                total += parseFloat(data.levels[1][i].sz);
              }
              return { price, size, total };
            });

            const orderbookData: OrderbookData = {
              bids: bids.slice(0, 20), // Top 20 bids
              asks: asks.slice(0, 20), // Top 20 asks
              timestamp: data.time,
            };

            // Notify all callbacks
            this.callbacks.forEach(callback => callback(orderbookData));
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = () => {
        console.log('WebSocket closed');
        this.isConnecting = false;
        this.ws = null;

        // Attempt to reconnect if we still have callbacks
        if (this.callbacks.size > 0 && this.currentSymbol) {
          this.reconnectTimer = setTimeout(() => {
            this.connect();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.isConnecting = false;
    }
  }

  private disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      // Only close if connection is OPEN or CLOSING, not if CONNECTING
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CLOSING) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.currentSymbol = null;
    this.currentDex = null;
    this.isConnecting = false;
  }

  cleanup() {
    this.callbacks.clear();
    this.disconnect();
  }
}

export const orderbookWs = new OrderbookWebSocket();
